// 页：数据健康
// spec §4.9 锚点：
//   - 数字化验收 4 项（上线 1 个月后必达）：PV 准确率 ≥99% / device_id 7d 持久度 ≥95% / click 落库 ≥99% / 实验分桶一致性 100%
//   - 写路径 spec：批量数组 POST ≤50 events → 维表 ID 解析 cache+ON DUPLICATE → saveAll，无回查无 UPDATE，删 PUT 端点
//   - 月分区（PK 必含 event_time），DROP PARTITION 秒级归档
//   - bot 过滤后才入分析

import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Tag, Table, Progress, Alert, Tooltip, Select, Button } from 'antd';
import { CheckCircleTwoTone, WarningTwoTone, CloseCircleTwoTone, ApiOutlined, ThunderboltOutlined, BulbOutlined } from '@ant-design/icons';
import { idpDataHealth } from '../data';
import type { IdpDataHealthDTO } from '@/services/idpAnalytics';
import { loadIdpHealthSnapshots } from '../healthSnapshots';

const SpecCallout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Alert
    type="info"
    showIcon
    icon={<ApiOutlined />}
    message={<span style={{ fontSize: 12 }}>{children}</span>}
    style={{ marginBottom: 12 }}
  />
);

type DataHealthProps = {
  initialSite?: string;
};

const DataHealth: React.FC<DataHealthProps> = ({ initialSite = 'all' }) => {
  const dh = idpDataHealth;
  const [filterSite, setFilterSite] = useState(initialSite);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | undefined>();
  const [apiHealth, setApiHealth] = useState<IdpDataHealthDTO[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setApiError(undefined);
    loadIdpHealthSnapshots(filterSite)
      .then(list => {
        if (cancelled) return;
        setApiHealth(list);
      })
      .catch(error => {
        if (cancelled) return;
        setApiHealth([]);
        setApiError(error?.message || 'IDP health API unavailable');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filterSite, refreshKey]);

  // spec acceptance helpers
  const acceptCheck = (val: number, target: number, label: string) => {
    const pass = val >= target;
    return {
      label,
      val,
      target,
      pass,
      icon: pass
        ? <CheckCircleTwoTone twoToneColor="#52c41a" />
        : Math.abs(val - target) < 0.01
          ? <WarningTwoTone twoToneColor="#fa8c16" />
          : <CloseCircleTwoTone twoToneColor="#cf1322" />,
      color: pass ? '#52c41a' : Math.abs(val - target) < 0.01 ? '#fa8c16' : '#cf1322',
    };
  };

  const accept = [
    acceptCheck(dh.spec_acceptance.pv_accuracy_vs_legacy, 0.99, '① PV 准确率（vs 旧表 idp_user_access_history）'),
    acceptCheck(dh.spec_acceptance.device_id_persistence_7d, 0.95, '② device_id 持久度 7d（UV 可计算）'),
    acceptCheck(dh.spec_acceptance.click_full_capture_rate, 0.99, '③ click 全采事件落库率'),
    acceptCheck(dh.spec_acceptance.experiment_bucket_consistency_7d, 1.0, '④ 实验分桶一致性 7d（同 device 100% 同桶）'),
  ];

  const overallPass = accept.every(a => a.pass);

  return (
    <div style={{ padding: '16px 24px' }}>
      {/* 顶部：spec 锚点 */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row align="middle" gutter={12}>
          <Col flex="100px"><strong>🩺 数据健康</strong></Col>
          <Col flex="auto">
            <span style={{ fontSize: 11, color: '#8c8c8c' }}>
              spec §4.9 写路径：前端批量数组 POST ≤50/请求 → 维表 ID 解析 cache+ON DUPLICATE → saveAll · 无回查无 UPDATE · 删 PUT 端点
            </span>
          </Col>
          <Col>
            <Tooltip title={apiError || 'GET /idp/health/snapshot'}>
              <Tag color={loading ? 'processing' : apiHealth.length ? 'success' : 'warning'}>
                {loading ? '加载中' : apiHealth.length ? '真实 API' : 'Mock 兜底'}
              </Tag>
            </Tooltip>
          </Col>
          <Col>
            <span style={{ marginRight: 4 }}>站点:</span>
            <Select value={filterSite} onChange={setFilterSite} style={{ width: 100 }} size="small">
              <Select.Option value="all">全部</Select.Option>
              <Select.Option value="0">FG</Select.Option>
              <Select.Option value="1">ST</Select.Option>
              <Select.Option value="2">BF</Select.Option>
            </Select>
          </Col>
          <Col>
            <Button size="small" onClick={() => setRefreshKey(v => v + 1)}>刷新</Button>
          </Col>
        </Row>
      </Card>

      {apiHealth.length ? (
        <Card size="small" title="Gateway 实时健康快照" style={{ marginBottom: 12 }}>
          <Row gutter={12}>
            {apiHealth.map(item => {
              const warnLags = (item.rollupLags || []).filter(v => v.status !== 'OK').length;
              const warnFill = (item.fieldFillRates || []).filter(v => v.status !== 'OK').length;
              return (
                <Col span={8} key={item.webKey}>
                  <Card size="small" style={{ borderTop: `3px solid ${item.healthy ? '#52c41a' : '#fa8c16'}` }}>
                    <Row gutter={8}>
                      <Col span={8}>
                        <Statistic title={item.webKey} value={item.healthy ? 'OK' : 'WARN'} valueStyle={{ color: item.healthy ? '#52c41a' : '#fa8c16', fontSize: 20 }} />
                      </Col>
                      <Col span={8}>
                        <Statistic title="rollup WARN" value={warnLags} valueStyle={{ color: warnLags ? '#fa8c16' : '#52c41a', fontSize: 20 }} />
                      </Col>
                      <Col span={8}>
                        <Statistic title="fill WARN" value={warnFill} valueStyle={{ color: warnFill ? '#fa8c16' : '#52c41a', fontSize: 20 }} />
                      </Col>
                    </Row>
                    <div style={{ marginTop: 8 }}>
                      {(item.fieldFillRates || []).map(field => (
                        <div key={field.fieldName} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                          <code>{field.fieldName}</code>
                          <span style={{ color: field.status === 'OK' ? '#52c41a' : '#fa8c16' }}>
                            {((field.fillRate || 0) * 100).toFixed(1)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>
      ) : null}

      <Alert
        type="success"
        showIcon
        icon={<BulbOutlined />}
        message={<span style={{ fontSize: 12 }}>
          <strong>业务方怎么用：</strong>每周巡检 + 出问题时排查。头条 4 项验收红灯 → 流量监控本身不可信，所有其他 panel 的数字都要打问号；rollup 延迟超 SLA → 报表数据滞后业务方不知道；字段填充率塌方 → 埋点 SDK 出 bug；bot 过滤率突增 → 可能被刷流量；维表 cache hit 率 &lt;99% → 后端压力上涨。
        </span>}
        style={{ marginBottom: 12 }}
      />

      {/* spec 数字化验收 4 项 — 头条 */}
      <Card
        size="small"
        title={<span>📋 spec §4.9 数字化验收（上线 1 个月后必达）</span>}
        extra={
          <Tag color={overallPass ? 'success' : 'warning'} style={{ fontSize: 12 }}>
            {overallPass ? '4 项全过' : `${accept.filter(a => a.pass).length}/4 通过`}
          </Tag>
        }
        style={{ marginBottom: 12 }}
      >
        <Row gutter={12}>
          {accept.map((a, i) => (
            <Col span={6} key={i}>
              <Card size="small" style={{ borderTop: `3px solid ${a.color}` }}>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>{a.label}</div>
                <Statistic
                  value={(a.val * 100).toFixed(1)}
                  suffix="%"
                  prefix={a.icon}
                  valueStyle={{ color: a.color, fontSize: 24 }}
                />
                <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 4 }}>
                  目标 ≥ {(a.target * 100).toFixed(0)}% {a.pass ? '✓' : `差 ${((a.target - a.val) * 100).toFixed(2)}%`}
                </div>
                <Progress percent={a.val * 100} size="small" showInfo={false} strokeColor={a.color} style={{ marginTop: 4 }} />
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* rollup 延迟 */}
      <Card size="small" title="Rollup 延迟（5 张衍生表，SLA 5min）" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          {dh.rollup_lag.map(r => {
            const over = r.current_lag_min > r.sla_min;
            return (
              <Col span={4} key={r.table}>
                <Card size="small" style={{ borderTop: `3px solid ${over ? '#cf1322' : '#52c41a'}` }}>
                  <div style={{ fontSize: 10, color: '#8c8c8c' }}>{r.table}</div>
                  <Statistic
                    value={r.current_lag_min}
                    suffix="min"
                    valueStyle={{ color: over ? '#cf1322' : '#52c41a', fontSize: 20 }}
                  />
                  <div style={{ fontSize: 9, color: '#8c8c8c' }}>SLA {r.sla_min}min {over && <span style={{ color: '#cf1322' }}>⚠️ 超标</span>}</div>
                  {/* mini sparkline */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', height: 30, gap: 1, marginTop: 4 }}>
                    {r.history_24h.map((h, i) => {
                      const maxLag = Math.max(...r.history_24h.map(x => x.lag_min), r.sla_min);
                      const heightPct = (h.lag_min / maxLag) * 100;
                      return (
                        <Tooltip key={i} title={`${new Date(h.ts).getHours()}:00  ${h.lag_min}min`}>
                          <div style={{
                            flex: 1, height: `${heightPct}%`,
                            background: h.lag_min > r.sla_min ? '#cf1322' : '#52c41a',
                            opacity: 0.6, minHeight: 2,
                          }} />
                        </Tooltip>
                      );
                    })}
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Card>

      {/* 字段填充率 */}
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col span={12}>
          <Card size="small" title="字段填充率（idp_event 关键字段）">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <th style={{ padding: 4, textAlign: 'left' }}>字段</th>
                  <th style={{ padding: 4, textAlign: 'right' }}>填充率</th>
                  <th style={{ padding: 4, textAlign: 'right' }}>阈值</th>
                  <th style={{ padding: 4, textAlign: 'right' }}>状态</th>
                </tr>
              </thead>
              <tbody>
                {dh.field_fill_rate.map(f => {
                  const pass = f.fill_rate >= f.expected_threshold;
                  return (
                    <tr key={f.field} style={{ borderBottom: '1px dashed #f5f5f5' }}>
                      <td style={{ padding: 4 }}><code style={{ fontSize: 10 }}>{f.field}</code></td>
                      <td style={{ padding: 4, textAlign: 'right' }}>
                        <strong style={{ color: pass ? '#52c41a' : '#cf1322' }}>{(f.fill_rate * 100).toFixed(1)}%</strong>
                      </td>
                      <td style={{ padding: 4, textAlign: 'right', color: '#8c8c8c' }}>≥{(f.expected_threshold * 100).toFixed(0)}%</td>
                      <td style={{ padding: 4, textAlign: 'right' }}>
                        {pass ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CloseCircleTwoTone twoToneColor="#cf1322" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </Col>

        <Col span={12}>
          <Card size="small" title="月分区状态（idp_event）">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <th style={{ padding: 4, textAlign: 'left' }}>分区</th>
                  <th style={{ padding: 4, textAlign: 'right' }}>事件数</th>
                  <th style={{ padding: 4, textAlign: 'right' }}>大小</th>
                  <th style={{ padding: 4, textAlign: 'right' }}>状态</th>
                </tr>
              </thead>
              <tbody>
                {dh.partition_status.map(p => (
                  <tr key={p.partition} style={{ borderBottom: '1px dashed #f5f5f5' }}>
                    <td style={{ padding: 4, fontFamily: 'monospace' }}>{p.partition}</td>
                    <td style={{ padding: 4, textAlign: 'right' }}>{p.event_count.toLocaleString()}</td>
                    <td style={{ padding: 4, textAlign: 'right', color: '#8c8c8c' }}>
                      {p.bytes > 0 ? `${(p.bytes / 1024 / 1024).toFixed(0)} MB` : '-'}
                    </td>
                    <td style={{ padding: 4, textAlign: 'right' }}>
                      <Tag color={p.status === 'OK' ? 'success' : p.status === 'PENDING' ? 'processing' : 'error'}>
                        {p.status}
                      </Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 8 }}>
              PK 必含 event_time 分区键 · DROP PARTITION 秒级 · 归档 = 导 OSS parquet → DROP
            </div>
          </Card>
        </Col>
      </Row>

      {/* bot 过滤 */}
      <Card size="small" title="Bot 过滤效果（7d）" style={{ marginBottom: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <th style={{ padding: 4, textAlign: 'left' }}>日期</th>
              <th style={{ padding: 4, textAlign: 'right' }}>总请求</th>
              <th style={{ padding: 4, textAlign: 'right' }}>bot 过滤</th>
              <th style={{ padding: 4, textAlign: 'right' }}>内部 IP 过滤</th>
              <th style={{ padding: 4, textAlign: 'right' }}>真实用户事件</th>
              <th style={{ padding: 4, textAlign: 'right' }}>过滤率</th>
            </tr>
          </thead>
          <tbody>
            {dh.bot_filter.map(b => {
              const filterRate = (b.filtered_bot + b.filtered_internal) / b.total;
              return (
                <tr key={b.date} style={{ borderBottom: '1px dashed #f5f5f5' }}>
                  <td style={{ padding: 4 }}>{b.date}</td>
                  <td style={{ padding: 4, textAlign: 'right' }}>{b.total.toLocaleString()}</td>
                  <td style={{ padding: 4, textAlign: 'right', color: '#cf1322' }}>{b.filtered_bot.toLocaleString()}</td>
                  <td style={{ padding: 4, textAlign: 'right', color: '#fa8c16' }}>{b.filtered_internal.toLocaleString()}</td>
                  <td style={{ padding: 4, textAlign: 'right', color: '#52c41a' }}><strong>{b.real_users.toLocaleString()}</strong></td>
                  <td style={{ padding: 4, textAlign: 'right' }}>
                    <Tag color={filterRate > 0.15 ? 'red' : 'default'}>{(filterRate * 100).toFixed(1)}%</Tag>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* 维表 cache + beacon */}
      <Row gutter={12}>
        <Col span={14}>
          <Card size="small" title="维表 ID 解析 cache（spec：cache+ON DUPLICATE，零回查）">
            <Table
              size="small"
              pagination={false}
              rowKey="dim"
              dataSource={dh.dim_cache}
              columns={[
                { title: '维表', dataIndex: 'dim', render: t => <code style={{ fontSize: 11 }}>{t}</code> },
                { title: 'hit', dataIndex: 'hit', render: n => n.toLocaleString() },
                { title: 'miss', dataIndex: 'miss', render: n => <span style={{ color: '#fa8c16' }}>{n.toLocaleString()}</span> },
                {
                  title: 'hit rate',
                  dataIndex: 'hit_rate',
                  render: r => (
                    <span>
                      <Progress percent={r * 100} size="small" style={{ width: 100, marginRight: 6 }} strokeColor={r > 0.99 ? '#52c41a' : r > 0.95 ? '#fa8c16' : '#cf1322'} />
                      <strong>{(r * 100).toFixed(2)}%</strong>
                    </span>
                  ),
                },
                { title: 'P99 查询', dataIndex: 'p99_lookup_us', render: n => `${n} μs` },
              ]}
            />
            <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 8 }}>
              ON DUPLICATE KEY UPDATE 模式：新 URL/selector 首见时 INSERT，后续 hash 命中走 cache
            </div>
          </Card>
        </Col>

        <Col span={10}>
          <Card size="small" title={<span><ThunderboltOutlined /> sendBeacon 队列（前端批量上报）</span>}>
            <Row gutter={8}>
              <Col span={8}><Statistic title="P50 延迟" value={dh.beacon_queue.p50_ms} suffix="ms" valueStyle={{ color: '#52c41a' }} /></Col>
              <Col span={8}><Statistic title="P95 延迟" value={dh.beacon_queue.p95_ms} suffix="ms" valueStyle={{ color: '#1890ff' }} /></Col>
              <Col span={8}><Statistic title="P99 延迟" value={dh.beacon_queue.p99_ms} suffix="ms" valueStyle={{ color: dh.beacon_queue.p99_ms > 200 ? '#fa8c16' : '#1890ff' }} /></Col>
            </Row>
            <Row gutter={8} style={{ marginTop: 12 }}>
              <Col span={12}><Statistic title="当前队列深度" value={dh.beacon_queue.queue_depth} suffix="events" /></Col>
              <Col span={12}><Statistic title="24h 丢弃数" value={dh.beacon_queue.drop_count_24h} suffix="events" valueStyle={{ color: dh.beacon_queue.drop_count_24h > 100 ? '#cf1322' : '#fa8c16' }} /></Col>
            </Row>
            <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 8 }}>
              spec：批量数组 POST ≤ 50 events/请求 · sendBeacon 兼容（text/plain 免 preflight）
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DataHealth;
